import { useState, useEffect } from 'react';
import { X, Search } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useServerList } from '../../hooks/useServerList';

export type AudienceType = 'all_ams' | 'all_physicians' | 'all' | 'pods';

interface AudienceSelectorProps {
  audienceType: AudienceType;
  selectedAmIds: string[];
  onAudienceTypeChange: (type: AudienceType) => void;
  onSelectedAmIdsChange: (ids: string[]) => void;
}

interface AMUser {
  _id: string;
  email: string;
  profile: { firstName: string; lastName: string };
}

export function AudienceSelector({
  audienceType,
  selectedAmIds,
  onAudienceTypeChange,
  onSelectedAmIdsChange
}: AudienceSelectorProps) {
  // Active AMs, searched on the server one page at a time. (This used to call
  // GET /users, which doesn't exist, so the list was always empty; and there
  // are more AMs than one page holds, so it must search rather than list.)
  const amList = useServerList<AMUser>({
    enabled: audienceType === 'pods',
    fetchPage: ({ page, pageSize, search, signal }) =>
      apiClient.searchUsersPage({ role: 'distributor', status: 'active', page, pageSize, search, signal }),
  });
  const ams = amList.items;
  const loading = amList.loading && ams.length === 0;
  // Labels for the chips: selected AMs may not be on the page in view (or,
  // for a saved blast, may never have been loaded at all).
  const [known, setKnown] = useState<Record<string, AMUser>>({});

  useEffect(() => {
    if (ams.length) setKnown(prev => ({ ...prev, ...Object.fromEntries(ams.map(am => [am._id, am])) }));
  }, [ams]);

  useEffect(() => {
    const missing = selectedAmIds.filter(id => !known[id]);
    if (audienceType !== 'pods' || missing.length === 0) return;
    let cancelled = false;
    Promise.all(missing.map(id => apiClient.getUserProfile(id).catch(() => null))).then(results => {
      if (cancelled) return;
      const found = results.flatMap(r => (r?.success && r.data ? [r.data as unknown as AMUser] : []));
      if (found.length) setKnown(prev => ({ ...prev, ...Object.fromEntries(found.map(am => [String(am._id), am])) }));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceType, selectedAmIds]);

  const handleToggleAm = (amId: string) => {
    if (selectedAmIds.includes(amId)) {
      onSelectedAmIdsChange(selectedAmIds.filter(id => id !== amId));
    } else {
      onSelectedAmIdsChange([...selectedAmIds, amId]);
    }
  };

  const handleRemoveAm = (amId: string) => {
    onSelectedAmIdsChange(selectedAmIds.filter(id => id !== amId));
  };

  const options: { value: AudienceType; label: string; description: string }[] = [
    { value: 'all_ams', label: 'All Account Managers', description: 'Send to all active AMs' },
    { value: 'all_physicians', label: 'All Physicians', description: 'Send to all active physicians' },
    { value: 'all', label: 'All Users', description: 'Send to all active AMs and physicians' },
    { value: 'pods', label: 'Specific Pods', description: 'Select AMs and include their linked physicians' }
  ];

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
        Target Audience
      </label>

      <div className="space-y-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
              audienceType === option.value
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="audienceType"
              value={option.value}
              checked={audienceType === option.value}
              onChange={() => onAudienceTypeChange(option.value)}
              className="mt-0.5 mr-3"
            />
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {option.label}
              </div>
              <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {option.description}
              </div>
            </div>
          </label>
        ))}
      </div>

      {audienceType === 'pods' && (
        <div className="mt-4">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Select Account Managers
          </label>

          {selectedAmIds.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedAmIds.map((amId) => {
                const am = known[amId];
                return (
                  <span
                    key={amId}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {am ? `${am.profile.firstName} ${am.profile.lastName}` : 'Account manager'}
                    <button
                      type="button"
                      onClick={() => handleRemoveAm(amId)}
                      className="hover:text-blue-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="relative mb-2">
            <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={amList.search}
              onChange={e => amList.setSearch(e.target.value)}
              placeholder="Search account managers..."
              aria-label="Search account managers"
              className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm"
              style={{ borderColor: 'var(--border-primary)' }}
            />
          </div>

          {loading ? (
            <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading account managers...</div>
          ) : (
            <div className="border rounded-lg max-h-48 overflow-y-auto" style={{ borderColor: 'var(--border-primary)' }}>
              {ams.map((am) => (
                <label
                  key={am._id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  style={{ borderColor: 'var(--border-primary)' }}
                >
                  <input
                    type="checkbox"
                    checked={selectedAmIds.includes(am._id)}
                    onChange={() => handleToggleAm(am._id)}
                    className="mr-3"
                  />
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      {am.profile.firstName} {am.profile.lastName}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                      {am.email}
                    </div>
                  </div>
                </label>
              ))}
              {ams.length === 0 && (
                <div className="px-3 py-4 text-sm text-center" style={{ color: 'var(--text-tertiary)' }}>
                  {amList.error ? amList.error : amList.search ? `No active account managers match "${amList.search}"` : 'No active account managers found'}
                </div>
              )}
              {ams.length > 0 && (amList.page > 1 || amList.hasMore) && (
                <div className="flex items-center justify-between px-3 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>
                  <span>
                    Page {amList.page}{amList.totalPages !== null ? ` of ${amList.totalPages}` : ''}
                    {amList.total !== null ? ` · ${amList.total} account managers` : ''}
                  </span>
                  <span className="flex gap-2">
                    <button type="button" disabled={amList.page <= 1} onClick={() => amList.setPage(amList.page - 1)} className="underline disabled:opacity-40 disabled:no-underline">Prev</button>
                    <button type="button" disabled={!amList.hasMore} onClick={() => amList.setPage(amList.page + 1)} className="underline disabled:opacity-40 disabled:no-underline">Next</button>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
